import React, { useEffect, useState, useMemo, useRef } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, Plus, Trash2, Users, History, AlertTriangle, MapPin, Car } from "lucide-react";
import { motion } from "framer-motion";
import { Skeleton, SkeletonText, SkeletonTable } from "@/components/ui/skeleton";

function to12h(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mStr} ${ampm}`;
}

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const THIS_MONTH = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

const BODY_TYPES = [
  { value: "sedan", label: "Sedan" },
  { value: "coupe", label: "Coupe" },
  { value: "hatchback", label: "Hatchback" },
  { value: "wagon", label: "Wagon" },
  { value: "suv-2row", label: "SUV (2-row)" },
  { value: "suv-3row", label: "SUV (3-row)" },
  { value: "minivan", label: "Minivan" },
  { value: "pickup", label: "Pickup Truck" },
  { value: "convertible", label: "Convertible" },
];

/** Inline autocomplete used for make/model */
function Autocomplete({ value, onChange, options, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = (value || "").toLowerCase().trim();
    if (!q) return options.slice(0, 60);
    return options.filter((o) => o.toLowerCase().includes(q)).slice(0, 60);
  }, [value, options]);
  return (
    <div className="relative">
      <input
        className="input-luxe w-full"
        value={value || ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute top-full left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-xl shadow-2xl"
          style={{ background: "#0A0A0A" }}>
          {filtered.map((opt) => (
            <li key={opt}
              className="px-4 py-2.5 cursor-pointer text-sm text-foreground hover:bg-foreground/10 hover:text-gold"
              onMouseDown={(e) => { e.preventDefault(); onChange(opt); setOpen(false); }}>
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Address autocomplete backed by /places/autocomplete (Google Places proxy) */
function AddressAutocomplete({ value, onChange }) {
  const [text, setText] = useState(value || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = text.trim();
    if (q.length < 3) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/places/autocomplete", { params: { q } });
        setResults(data.results || []);
      } catch (_) { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [text]);

  const pick = (r) => {
    setText(r.label);
    onChange(r.label);
    setResults([]);
  };

  return (
    <div className="relative">
      <MapPin size={14} className="absolute left-3 top-[14px] text-muted pointer-events-none" />
      <input
        className="input-luxe pl-9"
        value={text}
        placeholder="Start typing address…"
        onChange={(e) => { setText(e.target.value); onChange(e.target.value); }}
        onBlur={() => setTimeout(() => setResults([]), 200)}
        autoComplete="off"
      />
      {loading && <Loader2 size={13} className="absolute right-3 top-[14px] text-muted animate-spin" />}
      {results.length > 0 && (
        <ul className="absolute top-full left-0 right-0 z-50 mt-1 max-h-52 overflow-y-auto rounded-xl shadow-2xl"
          style={{ background: "#0A0A0A" }}>
          {results.map((r) => (
            <li key={r.place_id || r.label}
              className="px-4 py-2.5 cursor-pointer text-sm text-foreground hover:bg-foreground/10 flex gap-2 items-start"
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}>
              <MapPin size={13} className="text-muted mt-0.5 shrink-0" />
              <span>{r.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const EMPTY_FORM = {
  customer_name: "", customer_email: "", customer_phone: "",
  car_make: "", car_model: "", body_type: "sedan", car_year: "",
  appointment_date: fmtDate(new Date()), appointment_time: "09:00",
  price: 140, address: "", notes: "", splits: [],
};

export default function AdminPayroll() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [earningsTab, setEarningsTab] = useState("season");
  const [pastOpen, setPastOpen] = useState(false);
  const [pastCreated, setPastCreated] = useState(null);
  const [pastForm, setPastForm] = useState(EMPTY_FORM);

  // NHTSA data
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get("/admin/payroll/summary"), api.get("/admin/users")]);
      setSummary(s.data);
      setAdmins(u.data.users || []);
    } catch (e) { toast.error(errMsg(e, "Couldn't load payroll")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Load NHTSA makes on mount
  useEffect(() => {
    api.get("/cars/makes").then(({ data }) => setMakes(data.makes || [])).catch(() => {});
  }, []);

  // Load models when make is set
  useEffect(() => {
    const make = pastForm.car_make.trim();
    if (!make || !makes.includes(make)) { setModels([]); return; }
    setLoadingModels(true);
    api.get("/cars/models", { params: { make } })
      .then(({ data }) => setModels(data.models || []))
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false));
  }, [pastForm.car_make, makes]);

  // Auto-detect body type
  useEffect(() => {
    const { car_make, car_model } = pastForm;
    if (!car_make || !car_model || !makes.includes(car_make)) return;
    api.get("/cars/body-type", { params: { make: car_make, model: car_model } })
      .then(({ data }) => { if (data.body_type) setPastForm((f) => ({ ...f, body_type: data.body_type })); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastForm.car_model]);

  const isOwner = summary?.viewer_role === "owner";
  const viewerUid = summary?.viewer_uid;

  const activeEarnings = earningsTab === "season"
    ? (summary?.season_earnings || [])
    : (summary?.all_time_earnings || []);
  const activeDebt = earningsTab === "season"
    ? (summary?.season_debt || {})
    : (summary?.all_time_debt || {});

  const myBookingDates = useMemo(() => {
    if (!summary) return new Set();
    const s = new Set();
    for (const b of summary.season_bookings || []) {
      if (!b.appointment_date) continue;
      if (isOwner) s.add(b.appointment_date);
      else if ((b.splits || []).some((sp) => sp.uid === viewerUid)) s.add(b.appointment_date);
    }
    return s;
  }, [summary, isOwner, viewerUid]);

  useEffect(() => {
    if (!selectedDate) { setDayData(null); return; }
    const ds = fmtDate(selectedDate);
    setDayLoading(true);
    api.get(`/admin/payroll/day?date_str=${ds}`)
      .then(({ data }) => setDayData(data))
      .catch(() => setDayData(null))
      .finally(() => setDayLoading(false));
  }, [selectedDate]);

  const addSplit = () => setPastForm((f) => ({ ...f, splits: [...f.splits, { uid: "", email: "", percent: 0 }] }));
  const updateSplit = (i, k, v) =>
    setPastForm((f) => {
      const splits = [...f.splits];
      if (k === "uid") {
        const u = admins.find((a) => a.uid === v);
        splits[i] = { uid: v, email: u?.email || "", percent: splits[i].percent };
      } else {
        splits[i] = { ...splits[i], [k]: v };
      }
      return { ...f, splits };
    });
  const removeSplit = (i) => setPastForm((f) => ({ ...f, splits: f.splits.filter((_, idx) => idx !== i) }));

  const savePastBooking = async (e) => {
    e.preventDefault();
    const splits = pastForm.splits.map((s) => ({ ...s, percent: parseFloat(s.percent) || 0 }));
    const total = splits.reduce((a, b) => a + b.percent, 0);
    if (Math.abs(total - 100) > 0.5) return toast.error(`Splits must total 100% (got ${total.toFixed(0)}%)`);
    try {
      const payload = {
        customer_name: pastForm.customer_name,
        customer_email: pastForm.customer_email,
        customer_phone: pastForm.customer_phone,
        car_make: pastForm.car_make,
        car_model: pastForm.car_model,
        body_type: pastForm.body_type,
        car_year: pastForm.car_year ? parseInt(pastForm.car_year, 10) : null,
        appointment_date: pastForm.appointment_date,
        appointment_time: pastForm.appointment_time,
        price: parseFloat(pastForm.price),
        address: pastForm.address,
        notes: pastForm.notes,
        splits,
      };
      const { data } = await api.post("/admin/past-bookings", payload);
      toast.success("Past booking logged");
      setPastOpen(false);
      setPastCreated(data);
      setPastForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(errMsg(err, "Failed")); }
  };

  if (loading || !summary) {
    return (
      <div data-testid="admin-payroll">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
          <div>
            <Skeleton className="h-3 w-16 mb-2" />
            <Skeleton className="h-10 w-64" />
          </div>
          <Skeleton className="h-10 w-40 rounded-full" />
        </div>
        <div className="card-luxe p-6 mb-6">
          <Skeleton className="h-3 w-48 mb-4" />
          <div className="flex flex-wrap gap-x-4 gap-y-3 items-baseline text-sm">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-4 w-3" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-4 w-3" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="my-5 h-px w-full" style={{ background: "rgba(255,255,255,0.06)" }} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-5 w-20" />
              </div>
            ))}
          </div>
        </div>
        <div className="card-luxe p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-7 w-48 rounded-lg" />
          </div>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="py-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-48" />
                <div className="flex items-center gap-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-luxe p-4">
            <Skeleton className="h-3 w-20 mb-3" />
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 35 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </div>
          <div className="card-luxe p-5 min-h-[260px]">
            <Skeleton className="h-4 w-1/3 mb-3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-payroll">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
        <div>
          <p className="overline mb-2">Payroll</p>
          <h1 className="font-sans font-semibold text-4xl text-foreground tracking-[-0.02em]">Earnings & payroll</h1>
        </div>
        <button className="btn-secondary" onClick={() => setPastOpen(true)} data-testid="payroll-add-past">
          <History size={14} /> Log past booking
        </button>
      </div>

      {/* Season stats */}
      <div className="card-luxe p-6 mb-6" data-testid="payroll-breakdown">
        <p className="overline mb-4">Season {summary.season.year} · {summary.season.start} → {summary.season.end}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-3 items-baseline text-sm">
          <span style={{ color: "#6e6e6e" }}>Revenue</span>
          <span className="font-mono text-foreground text-lg">{money(summary.season_revenue)}</span>
          <span className="text-muted">−</span>
          <span className="text-muted">Expenses</span>
          <span className="font-mono text-foreground text-lg">{money(summary.season_expenses)}</span>
          <span className="text-muted">=</span>
          <span className="font-mono font-semibold text-lg" style={{ color: "var(--accent)" }}>{money(summary.season_net)}</span>
          <span className="overline ml-2">Net</span>
        </div>
        <div className="subtle-divider my-5" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Stat label="Revenue (season)" value={money(summary.season_revenue)} />
          <Stat label="Expenses (season)" value={money(summary.season_expenses)} />
          <Stat label="Revenue (all-time)" value={money(summary.all_time_revenue)} />
          <Stat label="Expenses (all-time)" value={money(summary.all_time_expenses)} />
        </div>
      </div>

      {/* Earnings with Season / All Time tabs */}
      <div className="card-luxe p-6 mb-6" data-testid="payroll-earnings">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-foreground" />
            <p className="overline">{isOwner ? "Everyone's earnings" : "Your earnings"}</p>
          </div>
          <div className="flex rounded-lg overflow-hidden text-xs">
            {[["season", `Season ${summary.season.year}`], ["alltime", "All Time"]].map(([key, label]) => (
              <button key={key} onClick={() => { setEarningsTab(key); setExpanded(null); }}
                className="px-3 py-1.5 transition-colors"
                style={{
                  background: earningsTab === key ? "rgba(138,138,138,0.15)" : "transparent",
                  color: earningsTab === key ? "#8A8A8A" : "var(--muted)",
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {activeEarnings.length === 0 ? (
          <p className="text-muted text-sm">No earnings yet for this period.</p>
        ) : (
          <div className="space-y-2">
            {activeEarnings.map((u) => {
              const userDebt = activeDebt[u.uid];
              const outstanding = userDebt?.outstanding || 0;
              const expenses = u.expenses || 0;
              const netPayout = u.net ?? Math.max(0, u.gross - expenses);
              return (
                <div key={u.uid}>
                  <button className="w-full text-left flex items-center justify-between p-3 rounded-lg hover:bg-foreground/[0.04] transition-colors"
                    onClick={() => setExpanded(expanded === u.uid ? null : u.uid)}
                    data-testid={`earnings-user-${u.uid}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-foreground font-medium">{u.email || u.uid}</span>
                      {outstanding > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{ color: "#FF9800", background: "rgba(255,152,0,0.08)" }}>
                          <AlertTriangle size={10} />{money(outstanding)} debt
                        </span>
                      )}
                    </div>
                    <span className="flex items-center gap-4 text-sm font-mono">
                      <span className="text-muted">{money(u.gross)} gross</span>
                      {expenses > 0 && <span style={{ color: "#FF9800" }}>−{money(expenses)}</span>}
                      <span className="text-foreground text-base">{money(netPayout)}</span>
                    </span>
                  </button>
                  {expanded === u.uid && (
                    <div className="px-3 pb-3 pt-1 space-y-1 text-sm ml-3">
                      {u.bookings.length === 0 ? (
                        <p className="text-faint italic">No bookings this period.</p>
                      ) : u.bookings.map((b) => {
                        const debtApplied = b.debt_applied || 0;
                        const netPay = b.net_payout ?? b.share;
                        const isCompleted = b.status === "completed";
                        return (
                          <div key={b.booking_id} className="text-muted space-y-0.5">
                            <div className="flex justify-between">
                              <span>
                                {b.date} · <span className="font-mono">{b.booking_id}</span> · {b.percent}% of {money(b.amount)}
                                {isCompleted && <span style={{ color: "#8A8A8A" }}> · completed</span>}
                              </span>
                              <span className="font-mono text-foreground">{money(b.share)}</span>
                            </div>
                            {isCompleted && debtApplied > 0 && (
                              <div className="flex justify-between text-[11px] pl-2">
                                <span style={{ color: "#FF9800" }}>− debt applied</span>
                                <span className="font-mono" style={{ color: "#FF9800" }}>−{money(debtApplied)}</span>
                              </div>
                            )}
                            {isCompleted && (
                              <div className="flex justify-between text-[11px] pl-2">
                                <span className="text-faint">= net payout{debtApplied > 0 && netPay === 0 && " · fully applied to debt"}</span>
                                <span className="font-mono" style={{ color: netPay === 0 && debtApplied > 0 ? "#FF9800" : "#4CAF50" }}>{money(netPay)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {outstanding > 0 && (
                        <div className="mt-2 p-2 rounded" style={{ background: "rgba(255,152,0,0.06)" }}>
                          <p className="text-xs" style={{ color: "#FF9800" }}>
                            <AlertTriangle size={11} className="inline mr-1" />
                            Outstanding debt: {money(outstanding)} — deducted from upcoming payouts
                          </p>
                          {userDebt?.expenses?.map((ex) => (
                            <p key={ex.expense_id} className="text-[11px] text-faint mt-1">
                              {ex.date} · {ex.item} · {money(ex.outstanding)} remaining
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Calendar + day view */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-luxe p-4">
          <p className="overline mb-1">Calendar</p>
          <p className="text-xs text-faint mb-3">{isOwner ? "Gold = days with any booking" : "Gold = your scheduled jobs"}</p>
          <Calendar
            mode="single"
            selected={selectedDate ?? undefined}
            onSelect={(d) => {
              if (!d) return;
              if (selectedDate && fmtDate(d) === fmtDate(selectedDate)) setSelectedDate(null);
              else setSelectedDate(d);
            }}
            defaultMonth={THIS_MONTH}
            modifiers={{ myBooking: (d) => myBookingDates.has(fmtDate(d)) }}
            modifiersStyles={{ myBooking: { fontWeight: 700, color: "#8A8A8A" } }}
            className="bg-transparent text-foreground"
          />
          {selectedDate && (
            <button className="text-xs text-faint hover:text-foreground mt-2 block"
              onClick={() => { setSelectedDate(null); setDayData(null); }}>Clear selection</button>
          )}
        </div>
        <div className="card-luxe p-5 min-h-[260px]">
          {!selectedDate ? (
            <div className="flex items-center justify-center h-full py-10">
              <p className="text-muted text-sm">Click a date to see bookings.</p>
            </div>
          ) : dayLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="p-4 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <Skeleton className="h-3 w-24 mb-2" />
                      <Skeleton className="h-4 w-48 mb-1" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                    <Skeleton className="h-5 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="overline mb-3">{fmtDate(selectedDate)}</p>
              {!dayData || dayData.bookings.length === 0 ? (
                <p className="text-muted text-sm">{isOwner ? "No bookings on this day." : "No scheduled jobs on this day."}</p>
              ) : (
                <div className="space-y-3">
                  {dayData.bookings.map((b) => (
                    <div key={b.id} className="p-4 bg-foreground/[0.04] rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-mono text-foreground text-xs">{b.id}</p>
                          <p className="text-muted text-sm">{b.client || "—"} · {b.car}</p>
                          <p className="text-faint text-xs">{to12h(b.appointment_time)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-foreground">{money(b.price)}</p>
                          <span className="text-[10px] uppercase tracking-[0.15em]"
                            style={{ color: b.status === "completed" ? "#8A8A8A" : b.status === "confirmed" ? "#4CAF50" : "#FF9800" }}>
                            {b.status}
                          </span>
                        </div>
                      </div>
                      {isOwner && b.splits?.length > 0 && (
                        <div className="mt-2 pt-2 space-y-1">
                          {b.splits.map((s) => (
                            <div key={s.uid} className="flex justify-between text-xs">
                              <span className="text-muted">{s.email}</span>
                              <span className="font-mono" style={{ color: "#8A8A8A" }}>{s.percent}% = {money(s.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Log past booking modal ── */}
      {pastOpen && (
        <Modal onClose={() => setPastOpen(false)} title="Log past booking" testid="past-modal">
          <p className="text-muted text-sm mb-5">Creates a real booking record + Firebase account accessible via the status page.</p>
          <form onSubmit={savePastBooking} className="space-y-4">

            {/* Customer info */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Customer name" required>
                <input className="input-luxe" required value={pastForm.customer_name}
                  onChange={(e) => setPastForm({ ...pastForm, customer_name: e.target.value })} data-testid="past-customer" />
              </FormField>
              <FormField label="Amount paid ($)" required>
                <input type="number" step="0.01" className="input-luxe" required value={pastForm.price}
                  onChange={(e) => setPastForm({ ...pastForm, price: e.target.value })} data-testid="past-price" />
              </FormField>
              <FormField label="Customer email (optional)">
                <input type="email" className="input-luxe" value={pastForm.customer_email}
                  onChange={(e) => setPastForm({ ...pastForm, customer_email: e.target.value })} placeholder="for status page" />
              </FormField>
              <FormField label="Customer phone (optional)">
                <input className="input-luxe" value={pastForm.customer_phone}
                  onChange={(e) => setPastForm({ ...pastForm, customer_phone: e.target.value })} placeholder="(555) 000-0000" />
              </FormField>
            </div>

            {/* Vehicle — NHTSA */}
            <div>
              <p className="overline mb-2 flex items-center gap-2"><Car size={12} /> Vehicle (NHTSA)</p>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Make" required>
                  <Autocomplete value={pastForm.car_make}
                    onChange={(v) => setPastForm((f) => ({ ...f, car_make: v, car_model: "", body_type: "sedan" }))}
                    options={makes} placeholder="e.g. Honda" />
                </FormField>
                <FormField label="Model" required>
                  <Autocomplete value={pastForm.car_model}
                    onChange={(v) => setPastForm((f) => ({ ...f, car_model: v }))}
                    options={models}
                    placeholder={loadingModels ? "Loading…" : makes.includes(pastForm.car_make) ? "e.g. Civic" : "Enter make first"}
                    disabled={!pastForm.car_make} />
                </FormField>
                <FormField label="Year (optional)">
                  <input type="number" className="input-luxe" value={pastForm.car_year} min={1980} max={2030}
                    onChange={(e) => setPastForm({ ...pastForm, car_year: e.target.value })} placeholder="2022" />
                </FormField>
                <FormField label="Body type">
                  <select className="input-luxe" value={pastForm.body_type}
                    onChange={(e) => setPastForm({ ...pastForm, body_type: e.target.value })}>
                    {BODY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </FormField>
              </div>
            </div>

            {/* Address — Google Maps */}
            <FormField label="Service address (optional)" icon={<MapPin size={12} />}>
              <AddressAutocomplete value={pastForm.address}
                onChange={(v) => setPastForm({ ...pastForm, address: v })} />
            </FormField>

            {/* Date & time */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Date" required>
                <input type="date" className="input-luxe" required value={pastForm.appointment_date}
                  onChange={(e) => setPastForm({ ...pastForm, appointment_date: e.target.value })} data-testid="past-date" />
              </FormField>
              <FormField label="Time">
                <select className="input-luxe" value={pastForm.appointment_time}
                  onChange={(e) => setPastForm({ ...pastForm, appointment_time: e.target.value })}>
                  {Array.from({ length: 19 }, (_, i) => {
                    const h = 9 + Math.floor(i / 2);
                    const m = i % 2 === 0 ? "00" : "30";
                    return `${String(h).padStart(2, "0")}:${m}`;
                  }).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </FormField>
            </div>

            <FormField label="Notes (optional)">
              <textarea className="input-luxe" rows={2} value={pastForm.notes}
                onChange={(e) => setPastForm({ ...pastForm, notes: e.target.value })} placeholder="Any details about the job…" />
            </FormField>

            {/* Worker splits */}
            <div>
              <p className="overline mb-2">Worker splits (must total 100%)</p>
              <div className="space-y-2 mb-3">
                {pastForm.splits.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <select className="input-luxe flex-1" value={s.uid}
                      onChange={(e) => updateSplit(i, "uid", e.target.value)} data-testid={`past-split-uid-${i}`}>
                      <option value="">Pick worker…</option>
                      {admins.map((a) => <option key={a.uid} value={a.uid}>{a.email}</option>)}
                    </select>
                    <input type="number" min="0" max="100" step="1" className="input-luxe w-24" value={s.percent}
                      onChange={(e) => updateSplit(i, "percent", e.target.value)} placeholder="%" data-testid={`past-split-pct-${i}`} />
                    <button type="button" onClick={() => removeSplit(i)} className="text-faint hover:text-red-400 p-2">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn-secondary" style={{ padding: "8px 14px", fontSize: "12px" }} onClick={addSplit}>
                <Plus size={12} /> Add worker
              </button>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setPastOpen(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="past-submit">Log booking</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Booking created confirmation */}
      {pastCreated && (
        <Modal onClose={() => setPastCreated(null)} title="Booking logged" testid="past-created-modal">
          <p className="text-muted text-sm mb-5">Booking created and accessible via the status page.</p>
          <div className="p-5 mb-5" style={{ background: "rgba(138,138,138,0.08)" }}>
            <p className="text-xs uppercase tracking-[0.2em] text-faint mb-2">Booking Reference</p>
            <p className="font-mono text-2xl" style={{ color: "#8A8A8A" }}>{pastCreated.id}</p>
            <p className="text-xs text-faint mt-2">Give this to the customer to look up their booking.</p>
          </div>
          <div className="text-sm space-y-1 mb-5">
            <p className="text-muted"><span className="text-foreground">Customer:</span> {pastCreated.contact?.name}</p>
            <p className="text-muted"><span className="text-foreground">Vehicle:</span> {pastCreated.car?.make} {pastCreated.car?.model}</p>
            <p className="text-muted"><span className="text-foreground">Date:</span> {pastCreated.appointment_date} at {to12h(pastCreated.appointment_time)}</p>
            <p className="text-muted"><span className="text-foreground">Total:</span> ${pastCreated.price?.estimate}</p>
          </div>
          <a href={`https://niskyauto.live/status?id=${pastCreated.id}`}
            target="_blank" rel="noreferrer" className="btn-secondary block text-center" style={{ marginBottom: "12px" }}>
            Open Status Page ↗
          </a>
          <button className="btn-primary w-full justify-center" onClick={() => setPastCreated(null)}>Done</button>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-[0.18em] text-faint">{label}</p>
      <p className="font-mono text-foreground">{value}</p>
    </div>
  );
}

function FormField({ label, children, required }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}{required && <span className="text-red-400 ml-1">*</span>}</span>
      {children}
    </label>
  );
}

function Modal({ children, onClose, title, testid }) {
  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={testid}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative card-luxe p-7 max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <h3 className="font-sans font-semibold text-2xl text-foreground mb-5">{title}</h3>
        {children}
      </div>
    </motion.div>
  );
}
